import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { LogoFull } from "@/components/Logo";
import { buildWaLink } from "@/lib/whatsapp";
import { Menu, X } from "lucide-react";

const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { id: "services", label: t("nav.services") },
    { id: "how", label: t("nav.how") },
    { id: "coverage", label: t("nav.coverage") },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-[#E2DFD2]/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-center" data-testid="header-logo-link">
          <LogoFull />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-${l.id}`}
              className="text-sm font-semibold text-[#0B1320] hover:text-[#FF5A00] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="language-toggle"
            onClick={toggle}
            className="inline-flex items-center gap-1.5 h-10 px-3.5 sm:px-4 rounded-full border border-[#0B1320]/15 hover:border-[#0B1320] transition-colors text-sm font-bold"
          >
            <span className={lang === "en" ? "text-[#0B1320]" : "text-[#0B1320]/40"}>EN</span>
            <span className="text-[#0B1320]/30">/</span>
            <span className={lang === "ar" ? "text-[#0B1320]" : "text-[#0B1320]/40"}>عربي</span>
          </button>

          <a
            href={buildWaLink(t("whatsapp.msg_default"))}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-cta"
            className="hidden md:inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0B1320] text-white text-sm font-bold hover:bg-[#FF5A00] transition-colors"
          >
            {t("nav.book")}
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-full border border-[#0B1320]/15"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FAF9F6] border-t border-[#E2DFD2]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-base font-semibold text-[#0B1320]"
                data-testid={`mobile-nav-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                toggle();
                setOpen(false);
              }}
              className="self-start mt-2 inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-[#0B1320]/15 text-sm font-bold"
              data-testid="mobile-language-toggle"
            >
              <span className={lang === "en" ? "text-[#0B1320]" : "text-[#0B1320]/40"}>EN</span>
              <span className="text-[#0B1320]/30">/</span>
              <span className={lang === "ar" ? "text-[#0B1320]" : "text-[#0B1320]/40"}>عربي</span>
            </button>
            <a
              href={buildWaLink(t("whatsapp.msg_default"))}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-cta"
              className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full bg-[#0B1320] text-white text-sm font-bold"
            >
              {t("nav.book")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
